import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useToast } from '../context/ToastContext';


const initialMealPlan = { id: null, title: '', recipes: [], additionalItems: [], numberOfDays: 3, selectedAdditionalItemGroupIds: [], startDay: '' };

export const useMealPlan = ({ activeApi, token, setConfirmationModal, selectedIdsRef }) => {
  const { showToast } = useToast();
  const [mealplans, setMealplans] = useState([]);
  const [activeMealPlan, setActiveMealPlan] = useState(initialMealPlan);
  const [isMealPlanDirty, setIsMealPlanDirty] = useState(false);
  const [isEditMealplanModalOpen, setIsEditMealplanModalOpen] = useState(false);
  const [editingMealplan, setEditingMealplan] = useState(null);
  const [globalServingSize, setGlobalServingSize] = useState(4);
  const [mealPlanIngredients, setMealPlanIngredients] = useState([]);
  const [shoppingList, setShoppingList] = useState([]);
  const [mealplanSearchTerm, setMealplanSearchTerm] = useState('');
  const [mealplanPage, setMealplanPage] = useState(1);
  const activeRequestRef = useRef(0);
  const autosaveTimeoutRef = useRef(null);
  const hasCheckedForAutosave = useRef(false);

  const _doLoadMealPlan = useCallback(async (mealplan) => {
    setActiveMealPlan(mealplan);
    setMealPlanIngredients([]);
    setShoppingList(mealplan.shoppingList || []);
    setIsMealPlanDirty(false);
    const newGlobalServingSize = mealplan.recipes.length > 0 ? (mealplan.recipes[0].servingSize || 4) : 4;
    setGlobalServingSize(newGlobalServingSize);

  }, []);

  useEffect(() => {
    if (token) {
      // This should run every time the activeApi changes (e.g. for tour mode)
      activeApi.getMealplans().then(setMealplans).catch(error => {
        console.error("Error fetching mealplans:", error);
        showToast("Failed to load meal plans", "error");
      });

      // This should only run ONCE per session, not on every token refresh.
      if (!hasCheckedForAutosave.current) {
        hasCheckedForAutosave.current = true;
        activeApi.getAutoSavedMealPlan().then(autoSavedMealPlan => {
          if (autoSavedMealPlan) {
            setConfirmationModal({
              isOpen: true,
              message: 'You have unsaved changes from a previous session. Do you want to discard them? Your changes will be lost.',
              onConfirm: () => {
                activeApi.clearAutoSavedMealPlan();
                setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } });
              },
              onCancel: () => {
                _doLoadMealPlan(autoSavedMealPlan);
                setIsMealPlanDirty(true);
                setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } });
              }
            });
          }
        }).catch(error => {
          if (error.message.includes('No auto-saved meal plan found')) return;
          console.error("Error fetching auto-saved mealplan:", error);
          // Don't show toast for auto-save fetch error to avoid annoyance on load
        });
      }
    } else {
      // Reset the flag on logout
      hasCheckedForAutosave.current = false;
    }
  }, [token, activeApi, setConfirmationModal, _doLoadMealPlan]);

  useEffect(() => {
    if (isMealPlanDirty) {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
      autosaveTimeoutRef.current = setTimeout(() => {
        activeApi.autoSaveMealPlan({ ...activeMealPlan, selectedAdditionalItemGroupIds: selectedIdsRef.current || [] })
          .catch(error => {
            console.error("Auto-save failed:", error);
            showToast("Auto-save failed", "warning");
          });
      }, 1500);
    }
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [activeMealPlan, isMealPlanDirty, activeApi]);

  useEffect(() => {
    const requestId = ++activeRequestRef.current;

    const fetchIngredients = async () => {
      if (activeMealPlan.recipes.length > 0 || (activeMealPlan.additionalItems && activeMealPlan.additionalItems.length > 0)) {
        try {
          const ingredients = await activeApi.getMealplanIngredients(activeMealPlan);
          if (requestId === activeRequestRef.current) {
            setMealPlanIngredients(ingredients);
            // Filter shopping list to only include items that are still in the meal plan
            setShoppingList(prevShoppingList => {
              const ingredientKeys = new Set(ingredients.map(ing => `${ing.name}|${ing.unit}`));
              return prevShoppingList.filter(item => ingredientKeys.has(`${item.name}|${item.unit}`));
            });
          }
        } catch (error) {
          console.error("Error fetching ingredients:", error);
          showToast("Failed to calculate ingredients", "error");
          if (requestId === activeRequestRef.current) {
            setMealPlanIngredients([]);
            setShoppingList([]);
          }
        }
      } else if (requestId === activeRequestRef.current) {
        setMealPlanIngredients([]);
        setShoppingList([]);
      }
    };

    fetchIngredients();
  }, [activeMealPlan, activeApi]);



  const toggleRecipeInMealPlan = useCallback((recipe) => {
    setActiveMealPlan(prev => {
      // Always add a new instance, never remove here.
      // Generate a unique ID for the meal plan entry
      const instanceId = `${recipe.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const mealPlanRecipe = {
        id: instanceId,
        recipeId: recipe.id, // Store the reference to the original recipe ID
        originalServingSize: recipe.servingSize || 4,
        servingSize: globalServingSize,
        day: null,
        order: prev.recipes.filter(r => !r.day).length
      };

      return { ...prev, recipes: [...prev.recipes, mealPlanRecipe] };
    });
    setIsMealPlanDirty(true);
  }, [globalServingSize]);

  const removeRecipeFromMealPlan = useCallback((targetId) => {
    setActiveMealPlan(prev => ({
      ...prev,
      // Remove if it matches the instance ID (for meal plan removal) 
      // OR if it matches the recipeId (for database deletion cleanup)
      recipes: prev.recipes.filter(r => r.id !== targetId && r.recipeId !== targetId)
    }));
    setIsMealPlanDirty(true);
  }, []);

  const updateRecipeInMealPlan = useCallback((recipe) => {
    setActiveMealPlan(prev => ({
      ...prev,
      // Update if it matches recipeId (new format) OR id (legacy format)
      recipes: prev.recipes.map(r => ((r.recipeId === recipe.id) || (r.id === recipe.id)) ? { ...r, ...recipe, id: r.id } : r)
    }));
    setIsMealPlanDirty(true);
  }, []);

  const clearActiveMealPlan = useCallback((force = false) => {
    const clear = () => {
      activeRequestRef.current++;
      setActiveMealPlan(initialMealPlan);
      setMealPlanIngredients([]);
      setShoppingList([]);
      setIsMealPlanDirty(false);
      activeApi.clearAutoSavedMealPlan();
      setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } });
    };

    if (isMealPlanDirty && !force) {
      setConfirmationModal({
        isOpen: true,
        message: 'You have unsaved changes. Are you sure you want to close this meal plan and discard your changes?',
        onConfirm: () => clear()
      });
    } else {
      clear();
    }
  }, [isMealPlanDirty, setConfirmationModal, activeApi]);

  useEffect(() => {
    if (!activeMealPlan.id && activeMealPlan.recipes.length === 0 && (!activeMealPlan.additionalItems || activeMealPlan.additionalItems.length === 0) && isMealPlanDirty) {
      clearActiveMealPlan(true);
    }
  }, [activeMealPlan, isMealPlanDirty, clearActiveMealPlan]);

  const closeMealPlan = useCallback(() => {
    clearActiveMealPlan();
  }, [clearActiveMealPlan]);

  const unselectAllMealplans = useCallback(() => {
    // Unselect all meal plans safely
    clearActiveMealPlan(false);
  }, [clearActiveMealPlan]);

  const loadMealPlan = useCallback((mealplan) => {
    const load = () => {
      activeApi.clearAutoSavedMealPlan();
      _doLoadMealPlan(mealplan);
      setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } });
    };

    if (isMealPlanDirty) {
      setConfirmationModal({
        isOpen: true,
        message: 'You have unsaved changes. Are you sure you want to load this meal plan and discard your changes?',
        onConfirm: load
      });
    } else {
      load();
    }
  }, [isMealPlanDirty, setConfirmationModal, _doLoadMealPlan, activeApi]);

  const cancelEditingMealplan = useCallback(() => {
    setEditingMealplan(null);
    setIsEditMealplanModalOpen(false);
  }, []);

  const startEditingMealplan = useCallback((mealplan) => {
    setEditingMealplan(mealplan);
    setIsEditMealplanModalOpen(true);
  }, []);

  const deleteMealplan = useCallback((mealplanId) => {
    const mealplanToDelete = mealplans.find(m => m.id === mealplanId);
    const message = mealplanToDelete
      ? `Are you sure you want to delete meal plan '${mealplanToDelete.title}'?`
      : 'Are you sure you want to delete this mealplan?';

    setConfirmationModal({
      isOpen: true,
      message,
      onConfirm: () => {
        activeApi.deleteMealplanAPI(mealplanId)
          .then(() => {
            const newMealplans = mealplans.filter(c => c.id !== mealplanId);
            setMealplans(newMealplans);

            if (activeMealPlan.id === mealplanId) {
              clearActiveMealPlan(true);
            }

            const totalPages = Math.ceil(newMealplans.length / 5);
            if (mealplanPage > totalPages) {
              setMealplanPage(totalPages || 1);
            }
          })
          .finally(() => setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } }));
      }
    });
  }, [activeApi, mealplans, mealplanPage, activeMealPlan, clearActiveMealPlan, setConfirmationModal]);

  const editMealplan = useCallback(async (mealplan) => {
    if (mealplans.find(m => m.title.toLowerCase() === mealplan.title.toLowerCase() && m.id !== mealplan.id)) {
      return { error: 'A meal plan with this name already exists.' };
    }

    try {
      const data = await activeApi.updateMealplanAPI(mealplan.id, mealplan);
      setMealplans(prev => prev.map(m => (m.id === data.id ? data : m)));
      if (activeMealPlan.id === data.id) {
        loadMealPlan(data);
      }
      cancelEditingMealplan();
      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  }, [mealplans, activeMealPlan, activeApi, loadMealPlan, cancelEditingMealplan]);

  const addMealPlan = useCallback(async (title) => {
    if (mealplans.find(m => m.title.toLowerCase() === title.toLowerCase())) {
      return { error: 'A meal plan with this name already exists.' };
    }
    const newMealPlan = { ...initialMealPlan, title, id: null };
    try {
      const savedMealPlan = await activeApi.addMealplanAPI(newMealPlan);
      setMealplans(prev => [...prev, savedMealPlan]);
      loadMealPlan(savedMealPlan);
      return { success: true };
    } catch (error) {
      return { error: error.message };
    }
  }, [mealplans, activeApi, loadMealPlan]);

  const reorderMealPlanRecipe = useCallback((draggedId, targetId, targetDay, dropPosition) => {
    const insertAfter = dropPosition === 'bottom';
    setActiveMealPlan(prev => {
      let recipes = [...prev.recipes];
      const draggedRecipe = recipes.find(r => r.id === draggedId);
      if (!draggedRecipe) return prev;

      const newDay = targetId ? recipes.find(r => r.id === targetId)?.day : targetDay;
      const updatedDraggedRecipe = { ...draggedRecipe, day: newDay };
      let remainingRecipes = recipes.filter(r => r.id !== draggedId);

      if (targetId) {
        let targetIndex = remainingRecipes.findIndex(r => r.id === targetId);
        if (insertAfter) {
          targetIndex++;
        }
        remainingRecipes.splice(targetIndex !== -1 ? targetIndex : remainingRecipes.length, 0, updatedDraggedRecipe);
      } else {
        remainingRecipes.push(updatedDraggedRecipe);
      }

      const unassigned = remainingRecipes.filter(r => !r.day).map((r, i) => ({ ...r, order: i }));
      const byDay = {};
      remainingRecipes.filter(r => r.day).forEach(r => {
        if (!byDay[r.day]) byDay[r.day] = [];
        byDay[r.day].push(r);
      });

      let finalRecipes = [...unassigned];
      Object.keys(byDay).sort((a, b) => parseInt(a) - parseInt(b)).forEach(day => {
        const dayRecipes = byDay[day].map((r, i) => ({ ...r, order: i }));
        finalRecipes = [...finalRecipes, ...dayRecipes];
      });

      return { ...prev, recipes: finalRecipes };
    });
    setIsMealPlanDirty(true);
  }, []);

  const moveRecipe = useCallback((recipeId, direction) => {
    const { recipes, numberOfDays } = activeMealPlan;
    const orderedRecipes = [];
    for (let i = 1; i <= numberOfDays; i++) {
      orderedRecipes.push(...recipes.filter(r => r.day === i).sort((a, b) => a.order - b.order));
    }
    const unassigned = recipes.filter(r => !r.day).sort((a, b) => a.order - b.order);
    orderedRecipes.push(...unassigned);

    const currentIndex = orderedRecipes.findIndex(r => r.id === recipeId);
    if (currentIndex === -1) return;

    const recipeToMove = orderedRecipes[currentIndex];

    if (direction === 'up') {
      if (currentIndex === 0) {
        if (numberOfDays <= 0) {
          return;
        }
        if (recipeToMove.day === undefined || recipeToMove.day === null) {
          reorderMealPlanRecipe(recipeId, null, numberOfDays, 'bottom');
        } else if (recipeToMove.day > 1) {
          reorderMealPlanRecipe(recipeId, null, recipeToMove.day - 1, 'bottom');
        }
        return;
      }
      const targetRecipe = orderedRecipes[currentIndex - 1];
      const isFirstInDay = recipeToMove.order === 0;
      const isCrossingDays = recipeToMove.day !== targetRecipe.day;
      const dropPosition = (isFirstInDay && isCrossingDays) ? 'bottom' : 'top';
      if (isCrossingDays) {
        const moveToDay = recipeToMove.day ? recipeToMove.day - 1 : numberOfDays;
        reorderMealPlanRecipe(recipeId, null, moveToDay, dropPosition);
      } else {
        reorderMealPlanRecipe(recipeId, targetRecipe.id, targetRecipe.day, dropPosition);
      }
    } else { // down
      if (currentIndex === (orderedRecipes.length - 1)) {
        if (recipeToMove.day === undefined || recipeToMove.day === null) {
          return;
        }
        if (recipeToMove.day === numberOfDays) {
          reorderMealPlanRecipe(recipeId, null, null, 'top');
          return;
        }
        reorderMealPlanRecipe(recipeId, null, recipeToMove.day + 1, 'top');
        return;
      }
      const targetRecipe = orderedRecipes[currentIndex + 1];
      const recipesInSameDay = orderedRecipes.filter(r => r.day === recipeToMove.day);
      const isLastInDay = recipeToMove.order === recipesInSameDay.length - 1 && recipeToMove.day !== null;
      const isCrossingDays = recipeToMove.day !== targetRecipe.day;
      const dropPosition = (isLastInDay && isCrossingDays) ? 'top' : 'bottom';
      if (isCrossingDays) {
        const moveToDay = recipeToMove.day ? recipeToMove.day + 1 : numberOfDays;
        if (moveToDay > numberOfDays) {
          reorderMealPlanRecipe(recipeId, targetRecipe.id, null, 'top');
        } else if (targetRecipe.day === undefined || targetRecipe.day === null) {
          reorderMealPlanRecipe(recipeId, null, moveToDay, 'top');
        } else if (moveToDay < targetRecipe.day) {
          reorderMealPlanRecipe(recipeId, null, moveToDay, 'top');
        } else {
          reorderMealPlanRecipe(recipeId, targetRecipe.id, moveToDay, 'top');
        }
      } else {
        reorderMealPlanRecipe(recipeId, targetRecipe.id, targetRecipe.day, dropPosition);
      }
    }
  }, [activeMealPlan.recipes, activeMealPlan.numberOfDays, reorderMealPlanRecipe]);



  const updateNumberOfDays = useCallback((newNumberOfDays) => {
    setActiveMealPlan(prev => {
      const updatedRecipes = prev.recipes.map(recipe =>
        recipe.day > newNumberOfDays ? { ...recipe, day: null } : recipe
      );
      return { ...prev, numberOfDays: newNumberOfDays, recipes: updatedRecipes };
    });
    setIsMealPlanDirty(true);
  }, []);

  const updateServingSize = useCallback((recipeId, newSize, isGlobal) => {
    if (isGlobal) setGlobalServingSize(newSize);
    setActiveMealPlan(prev => ({
      ...prev,
      recipes: prev.recipes.map(r =>
        isGlobal || r.id === recipeId ? { ...r, servingSize: newSize } : r
      )
    }));
    setIsMealPlanDirty(true);
  }, []);

  const saveActiveMealPlan = useCallback(async (name, saveAs = false, options = {}) => {
    const performSave = async (mealPlanName, isSaveAs, existingId) => {
      const mealPlanToSave = { ...activeMealPlan, title: mealPlanName, shoppingList, selectedAdditionalItemGroupIds: selectedIdsRef.current || [] };
      if (isSaveAs) delete mealPlanToSave.id;
      if (existingId) mealPlanToSave.id = existingId;

      const isNew = (isSaveAs && !existingId) || (!isSaveAs && !activeMealPlan.id);
      const savedMealPlan = isNew
        ? await activeApi.addMealplanAPI(mealPlanToSave)
        : await activeApi.updateMealplanAPI(existingId || activeMealPlan.id, mealPlanToSave);

      setMealplans(prev => prev.some(mp => mp.id === savedMealPlan.id)
        ? prev.map(mp => mp.id === savedMealPlan.id ? savedMealPlan : mp)
        : [...prev, savedMealPlan]);

      setActiveMealPlan(savedMealPlan);
      setIsMealPlanDirty(false);
      await activeApi.clearAutoSavedMealPlan();
      if (options.onSuccess) {
        options.onSuccess();
      }
    };

    const existingMealplan = mealplans.find(mp => mp.title.toLowerCase() === name.toLowerCase());
    if (existingMealplan && (saveAs || !activeMealPlan.id)) {
      setConfirmationModal({
        isOpen: true,
        message: 'A meal plan with this name already exists. Do you want to overwrite it?',
        onConfirm: async () => {
          await performSave(name, saveAs, existingMealplan.id);
          setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { }, onCancel: () => { } });
        }
      });
    } else {
      await performSave(name, saveAs);
    }
  }, [activeApi, mealplans, activeMealPlan, shoppingList, setConfirmationModal, selectedIdsRef]);

  const addAdditionalItem = useCallback((item, source = 'manual') => {
    setActiveMealPlan(prev => {
      const additionalItems = prev.additionalItems ? [...prev.additionalItems] : [];
      const existingItemIndex = additionalItems.findIndex(i =>
        i.name.toLowerCase() === item.name.toLowerCase() && i.unit === item.unit
      );

      if (existingItemIndex > -1) {
        const existingItem = { ...additionalItems[existingItemIndex] };
        const sources = Array.isArray(existingItem.sources) ? existingItem.sources : [];
        if (!sources.includes(source)) {
          existingItem.sources = [...sources, source];
          additionalItems[existingItemIndex] = existingItem;
        }
      } else {
        additionalItems.push({
          ...item,
          id: Date.now() + Math.random(),
          sources: [source]
        });
      }
      return { ...prev, additionalItems };
    });
    setIsMealPlanDirty(true);
  }, []);

  const removeSourceFromAdditionalItem = useCallback((item, source) => {
    setActiveMealPlan(prev => {
      let additionalItems = prev.additionalItems ? [...prev.additionalItems] : [];
      const existingItemIndex = additionalItems.findIndex(i =>
        i.name.toLowerCase() === item.name.toLowerCase() && i.unit === item.unit
      );

      if (existingItemIndex > -1) {
        const existingItem = { ...additionalItems[existingItemIndex] };
        if (Array.isArray(existingItem.sources)) {
          existingItem.sources = existingItem.sources.filter(s => s !== source);

          if (existingItem.sources.length === 0) {
            additionalItems.splice(existingItemIndex, 1);
          } else {
            additionalItems[existingItemIndex] = existingItem;
          }
        }
      }
      return { ...prev, additionalItems };
    });
    setIsMealPlanDirty(true);
  }, []);

  const deleteAdditionalItem = useCallback((itemId) => {
    setActiveMealPlan(prev => ({ ...prev, additionalItems: prev.additionalItems.filter(item => item.id !== itemId) }));
    setIsMealPlanDirty(true);
  }, []);

  const updateAdditionalItem = useCallback((updatedItem) => {
    setActiveMealPlan(prev => ({ ...prev, additionalItems: prev.additionalItems.map(item => item.id === updatedItem.id ? updatedItem : item) }));
    setIsMealPlanDirty(true);
  }, []);

  const filteredMealplans = useMemo(() =>
    mealplans.filter(mealplan => mealplan.title && mealplan.title.toLowerCase().includes(mealplanSearchTerm.toLowerCase())),
    [mealplans, mealplanSearchTerm]
  );

  const handleSearchChange = useCallback((term) => {
    setMealplanSearchTerm(term);
    setMealplanPage(1);
  }, []);

  const updateStartDay = useCallback((newStartDay) => {
    setActiveMealPlan(prev => ({ ...prev, startDay: newStartDay }));
    setIsMealPlanDirty(true);
  }, []);

  return {
    mealplans,
    activeMealPlan,
    isMealPlanDirty,
    isEditMealplanModalOpen,
    editingMealplan,
    globalServingSize,
    mealPlanIngredients,
    shoppingList, setShoppingList,
    mealplanSearchTerm,
    mealplanPage, setMealplanPage,
    filteredMealplans,
    loadMealPlan,
    clearActiveMealPlan,
    closeMealPlan,
    toggleRecipeInMealPlan,
    removeRecipeFromMealPlan,
    updateRecipeInMealPlan,
    reorderMealPlanRecipe,
    moveRecipe,
    updateNumberOfDays,
    updateServingSize,
    saveActiveMealPlan,
    addAdditionalItem,
    removeSourceFromAdditionalItem,
    deleteAdditionalItem,
    updateAdditionalItem,
    deleteMealplan,
    editMealplan,
    addMealPlan,
    startEditingMealplan,
    cancelEditingMealplan,
    handleSearchChange,
    setIsMealPlanDirty,
    unselectAllMealplans,
    updateStartDay,
  };
};